import { alpha } from "@mui/material/styles";
import { tokens, SHADOW } from "../theme";

// Shared surface and motion recipes.
//
// These existed as copy-pasted `sx` blocks in five components — the same
// `transition: 'transform 0.3s ease-in-out'` / `translateY(-8px)` pair on the home
// feature cards, the home service cards, the About service cards, the project cards and
// (at -4px) the legal related-policy cards. Keeping one copy means the hover distance,
// the easing and the reduced-motion guard only have to be right once.

// Everything animates through the same curve. `ease-in-out` on a hover lift reads sluggish
// leaving and abrupt arriving; a standard-ease curve settles instead of bouncing.
export const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

/**
 * Card/panel surface: raised background, hairline edge, seated shadow.
 * `MuiCard` already applies the border and resting shadow, so this is for the plain
 * `Paper` and `Box` panels that do not go through that override.
 */
export const cardSurface = (theme) => {
  const colors = tokens(theme.palette.mode);
  return {
    backgroundColor: colors.background[300],
    border: `1px solid ${colors.background[400]}`,
    borderRadius: 4, // shape.borderRadius is MUI's default 4, so this is 16px
    boxShadow: SHADOW[theme.palette.mode].rest,
  };
};

/**
 * Hover treatment for an interactive card. The lift is 4px rather than the old 8px: at
 * 8px with no shadow change the card slid rather than rose, which is what made it read as
 * a bounce. Pairing a smaller rise with a deeper shadow sells the height instead.
 */
export const hoverLift = (theme) => ({
  transition: `transform .3s ${EASE}, box-shadow .3s ${EASE}, border-color .3s ${EASE}`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: SHADOW[theme.palette.mode].hover,
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
  [REDUCED_MOTION]: {
    transition: "none",
    "&:hover": { transform: "none" },
  },
});

/**
 * Slow zoom on an image inside a fixed-height frame. Apply to the frame; it drives any
 * `img` beneath it, so it works for both a bare `<img>` and MUI's `CardMedia`.
 */
export const imageZoom = {
  overflow: "hidden",
  "& img": {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: `transform .6s ${EASE}`,
  },
  "&:hover img": { transform: "scale(1.06)" },
  [REDUCED_MOTION]: {
    "& img": { transition: "none" },
    "&:hover img": { transform: "none" },
  },
};

/** Small tracked-out label that sits above a section heading. */
export const eyebrow = (theme) => ({
  display: "block",
  color: theme.palette.primary.main,
  fontSize: "0.8125rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  mb: 1.5,
});

/**
 * A tinted circular backing for an icon. Replaces the bare 40px icons that were floating
 * on the contact and home cards with no anchor.
 */
export const iconChip = (theme, size = 52) => ({
  width: size,
  height: size,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.1),
});

/**
 * The vertical rhythm every full-width band on the site uses.
 *
 * `SECTION_Y` is exported separately because `{ ...sectionPadding, pb: 0 }` does not do
 * what it looks like: MUI's spacing plugin resolves the shorthand `py` after the
 * individual sides, so `py` wins over a later `pb` no matter the key order. A band that
 * needs one side suppressed must spell out `pt: SECTION_Y, pb: 0` instead.
 */
export const SECTION_Y = { xs: 7, md: 11 };
export const sectionPadding = { py: SECTION_Y };
